'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { Invitation } from '@wonderschool/common-base-types';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { getPendingInvitation } from '../../../external-apis/sso';
import useAppLanguage from '../../../hooks/useAppLanguage';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import useLogger from '../../../rollbar/logger';
import { PUBLIC_ROUTES } from '../../../routes/routes';
import { stripTrailingSlash } from '../../../utils/utils';
import Loader from '../../Loader';
import Button from '../../Parts/v2/Button';
import FormField from '../../Parts/v2/FormField';
import FormInput from '../../Parts/v2/FormInput';
import { styles } from '../../Parts/v2/styles';
import useOnboarding from '../hooks/useOnboarding';
import { ANON_USER_KEYS, EmailFormSchema, FORM_FIELD_NAMES, SSO_ROUTES, USER_TYPE_ENUMS } from '../types';
import { REFERRAL_URL_KEY } from './Landing';
import OnboardHeader from './OnboardHeader';

const SSO_URL = process.env.NEXT_PUBLIC_WS_AUTH_URL ?? '';
const WS_MARKETPLACE_URL = process.env.NEXT_PUBLIC_WS_MARKETPLACE_URL ?? 'https://wonderschool.com';

export default function EmailForm() {
  const { translate, currentLanguage } = useAppLanguage();
  const { emailSchema } = useOnboarding();
  const router = useRouter();
  const searchParams = useSearchParams();
  const anonUserType = searchParams?.get(ANON_USER_KEYS.TYPE) as USER_TYPE_ENUMS | null;
  const networkId = searchParams?.get(ANON_USER_KEYS.NETWORK_ID);
  const ambassadorId = searchParams?.get('ambassadorId')?.trim();
  const contextParams = new URLSearchParams({
    ...(networkId ? { [ANON_USER_KEYS.NETWORK_ID]: networkId } : {}),
    ...(ambassadorId ? { ambassadorId } : {}),
  });
  const landingQuery = contextParams.toString();
  const [loading, setLoading] = useState(false);
  const { getLocalStorageValue } = useLocalStorage();

  const logger = useLogger();
  const methods = useForm<EmailFormSchema>({
    resolver: yupResolver(emailSchema),
    mode: 'onSubmit',
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    setValue,
    setError,
  } = methods;

  // If there is no user type, then we cannot proceed and need to restart
  useEffect(() => {
    if (!anonUserType) {
      router.replace(PUBLIC_ROUTES.LANDING);
    } else {
      setValue(FORM_FIELD_NAMES.USER_TYPE, anonUserType, { shouldValidate: true });
    }
  }, [router, anonUserType, setValue]);

  // If the user type is invalid, then we cannot proceed and need to restart
  useEffect(() => {
    if (errors[FORM_FIELD_NAMES.USER_TYPE]) {
      router.replace(PUBLIC_ROUTES.LANDING);
    }
  }, [errors, router]);

  // invitations contain the email, and they should be lowercase,
  // but it is absolutely critical that they remain so,
  // as such, to be safe, we require the lowercase email passed separately to be sure
  const handleInviteRedirect = (invitation: Invitation, email: string) => {
    const payload = {
      ...(invitation ? invitation : {}),
      phone: invitation.phone?.replace('+1', ''),
      email: email.toLowerCase(),
      lang: currentLanguage?.code ?? 'en',
    };

    const authority = `${stripTrailingSlash(SSO_URL)}`;
    const endpoint = SSO_ROUTES.INVITATION;
    const encoded = Buffer.from(JSON.stringify(payload)).toString('base64');
    /*
      NOTE: 
      * SSO invitation route expects route/encoded
      * SSO signup route expects route#encoded
    */
    const url = `${authority}/${endpoint}/${encoded}`;
    router.push(url);
  };

  const onSubmit = handleSubmit(async (data: EmailFormSchema) => {
    // CRITICAL: ALWAYS LOWERCASE THE EMAIL!
    const email = data.email.toLowerCase();

    setLoading(true);

    // 1. check for email usage first
    // 2. check for a pending invite
    // 3. otherwise, let the user continue to the next route

    try {
      const emailUsageApiResponse = await fetch('/api/fetch-email-usage', {
        method: 'POST',
        body: JSON.stringify({
          email,
        }),
      }).then((res) => res.json());

      if (!emailUsageApiResponse?.emailAvailable) {
        setError(FORM_FIELD_NAMES.EMAIL, {
          message: translate('This email address is already in use'),
        });
        setLoading(false);
        return null;
      }
    } catch (error) {
      setLoading(false);

      logger.error('error fetching email usage: ', {
        email,
        error,
      });
    }

    // check for invitation
    const invitation = await getPendingInvitation(email);

    // all invitations should be redirected to sso immediately
    if (invitation) {
      handleInviteRedirect(invitation, email);
      return null;
    }

    // parents immediately go to sso for signup and redirect to referral URL or marketplace
    if (anonUserType === USER_TYPE_ENUMS.FAMILY) {
      // Use stored referral URL (from document.referrer or explicit param) for auth return,
      // falling back to marketplace. This matches the hash structure Nova uses for the
      // favorite/bookmark signup flow which correctly preserves network subdomains.
      const storedReferralUrl = getLocalStorageValue(REFERRAL_URL_KEY);
      const authReturnUrl = storedReferralUrl || `${stripTrailingSlash(WS_MARKETPLACE_URL)}/auth-return`;

      // Distinguish between explicit partner referral URLs and our document.referrer
      // fallback (which always ends in /auth-return). Partner referrals need source
      // and redirectTo to work correctly with the accounts app.
      const isPartnerReferral = storedReferralUrl && !storedReferralUrl.endsWith('/auth-return');

      const payload = {
        ret: authReturnUrl,
        nos: false,
        rts: {
          ...(isPartnerReferral ? { source: 'referral_partner' } : {}),
          type: 'parent',
          mode: 'signup',
          email,
          nav: { route: '/parents' },
          ...(networkId ? { networkId } : {}),
          ...(isPartnerReferral ? { redirectTo: storedReferralUrl } : {}),
        },
        lang: currentLanguage?.code ?? 'en',
      };

      const encoded = Buffer.from(JSON.stringify(payload)).toString('base64');
      const url = `${stripTrailingSlash(SSO_URL)}/${SSO_ROUTES.SIGNUP}#${encoded}`;

      router.push(url);
      return null;
    }

    // new provider signups have one more step in onboarding so build the query and send it
    if (anonUserType === USER_TYPE_ENUMS.PROVIDER) {
      const providerParams = new URLSearchParams(contextParams);
      providerParams.set(ANON_USER_KEYS.TYPE, anonUserType);
      providerParams.set(ANON_USER_KEYS.EMAIL, email);

      // send to provider select page with "type" and "email" params set
      const url = `${PUBLIC_ROUTES.PROVIDER_SELECT}/?${providerParams.toString()}`;
      router.push(url);
    }
    return null;
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-16 max-sm:space-y-10">
      <OnboardHeader subtext="Let's get your account set up." />
      <div className="space-y-4">
        <FormProvider {...methods}>
          <form onSubmit={onSubmit} noValidate className="space-y-16" data-testid="sign-up-email-form">
            <FormInput hidden {...register(FORM_FIELD_NAMES.USER_TYPE)} />
            <FormField
              label="Email"
              data-testid="signup-email-input"
              error={errors[FORM_FIELD_NAMES.EMAIL]}
              placeholder="Email"
              type="email"
              required
              {...register(FORM_FIELD_NAMES.EMAIL)}
            />
            <Button type="submit" disabled={!isValid} text={translate('Next')} data-testid="sign-up-email-next" />
          </form>
          <div className={styles.btnLink}>
            <Link
              href={`${PUBLIC_ROUTES.LANDING}${landingQuery ? `?${landingQuery}` : ''}`}
              data-testid="sign-up-email-back"
            >
              {translate('Go back')}
            </Link>
          </div>
        </FormProvider>
      </div>
    </div>
  );
}
